import React, { useState } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';

import FormTextField from 'components/formComponents/FormTextField';
import {
  IPaymentTypes,
  NewSubscriptionParser,
} from 'pages/newSubscription/models';
import { NewSubscriptionApi } from 'api/newSubscriptionApi';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectPrice,
  selectRegistrationToken,
  selectSelectedPackages,
  selectValidPromoCode,
  setPoNumber,
  setFinanceEmail,
} from 'reducers/misc';
import { useLocation } from '@reach/router';
import { AppPath } from 'appConstants';
import { ThreeBounce } from 'better-react-spinkit';
import FormGeneralError from 'components/formComponents/FormGeneralError';
import { formatNumber, formatPrice } from 'utils/formating';
import { SubscribeApi } from 'api/subscribeApi';
import { useFetchPackages } from 'hooks/useFetchPackages';
import { PricingSummaryDetails } from 'components/pricingSummaryDetails/PricingSummaryDetails';

type PaymentTypeFormProps = {
  onSubmit: (typeOfPayment: 'card' | 'invoice') => void | null;
};

export const PaymentTypeForm: React.FC<PaymentTypeFormProps> = ({
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const pathname = location.pathname;

  const [error, setError] = useState<string>('');

  const promoCode = useSelector(selectValidPromoCode);
  const selectedPackages = useSelector(selectSelectedPackages);
  const price = useSelector(selectPrice);
  const registrationToken = useSelector(selectRegistrationToken);

  const { financeEmail } = useFetchPackages(pathname);

  const validationSchema = Yup.object({
    FinanceEmail: Yup.string()
      .required('Please enter an email')
      .email('Please enter a valid email'),
  });

  const subscribeByInvoice = async (
    setSubmitting: (status: boolean) => void,
    poNumber: string,
    FinanceEmail: string,
  ) => {
    setSubmitting(true);
    let result;
    if (AppPath.SUBSCRIBE === pathname) {
      result = await SubscribeApi.subscribeByInvoice(
        registrationToken,
        promoCode,
        selectedPackages,
        price.total,
        poNumber,
        FinanceEmail
      );
    } else {
      result = await NewSubscriptionApi.subscribeByInvoice(
        promoCode,
        selectedPackages,
        price.total,
        poNumber,
        FinanceEmail
      );
    }
    if (result.data) {
      onSubmit('invoice');
    }
    if (result.error) {
      setError(result.error.message);
    }
    setSubmitting(false);
  };

  const handleSubmitForm = (
    values: IPaymentTypes,
    { setSubmitting }: FormikHelpers<IPaymentTypes>
  ) => {
    if (!values.byCard && !values.byInvoice) return;

    const { poNumber, FinanceEmail } = values;

    if (values.byCard) {
      dispatch(setPoNumber(poNumber));
      dispatch(setFinanceEmail(FinanceEmail));
      setSubmitting(true);
      onSubmit('card');
    }

    if (values.byInvoice) {
      subscribeByInvoice(setSubmitting, poNumber, FinanceEmail);
    }
  };

  return (
    <div className="Form PaymentTypeForm">
      <Formik
        initialValues={NewSubscriptionParser.createEmptyPaymentType(financeEmail)}
        onSubmit={(values, formikHelpers) =>
          handleSubmitForm(values, formikHelpers)
        }
        validationSchema={validationSchema}
        enableReinitialize={true}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form>
            <div className="PricingColRow">
              <div className="PCRowInner">
                <div className="LeftCol">
                  <FormGeneralError message={error} />
                  <FormTextField
                    field={'poNumber'}
                    placeholder={'Enter PO number (optional)'}
                    required={false}
                  />
                  {/* <div className="PaymentTypeOption">
                    <FormCheckboxField
                      field={'byCard'}
                      label={'Pay by card'}
                      onlyOneOptionSelected={() => setFieldValue('byInvoice', false)}
                    />
                    <span>£{formatNumber(price.total)}</span>
                  </div> */}
                  {/* <div className="PaymentTypeOption">
                    <FormCheckboxField
                      field={'byInvoice'}
                      label={'Pay by invoice'}
                      onlyOneOptionSelected={() => setFieldValue('byCard', false)}
                    />
                    <span>£{formatNumber(price.total)}</span>
                  </div> */}
                  <div className="PayTypeOptRow">
                    <label className={'PaymentTypeOption'}>
                      <input
                        type="radio"
                        name="payment-type"
                        className="HiddenInput"
                        checked={values.byCard}
                        onChange={() => {
                          setFieldValue('byCard', true);
                          setFieldValue('byInvoice', false);
                        }}
                      />
                      <label className="CustomInput" htmlFor={'byCard'}>
                        Pay by card
                        <span>£{formatPrice(price.total)}</span>
                      </label>
                    </label>                  
                    <label className={'PaymentTypeOption'}>
                      <input
                        type="radio"
                        name="payment-type"
                        className="HiddenInput"
                        checked={values.byInvoice}
                        onChange={() => {
                          setFieldValue('byInvoice', true);
                          setFieldValue('byCard', false);
                        }}
                      />
                      <label className="CustomInput" htmlFor={'byInvoice'}>
                        Pay by invoice
                        <span>£{formatPrice(price.total)}</span>
                      </label>
                    </label>
                  </div>
                  <p>Enter email address to send card receipt / invoice to:</p>
                  <div className="FinanceEmailField">
                    <FormTextField
                      field={'FinanceEmail'}
                      placeholder={'Email address'}
                      required={true}
                      value={values.FinanceEmail}
                    />
                  </div>
                </div>
                <div className="RightCol">
                  <PricingSummaryDetails 
                    addPromoStep={false}
                    isDisabled={isSubmitting || (!values.byCard && !values.byInvoice) || values.FinanceEmail === ""}
                    isSubmitting={isSubmitting}
                    btnText="Save and continue"
                  />
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
